import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import { useTheme } from '../../context/ThemeContext';
import ProfileDrawer from '../../components/ProfileDrawer';
import { IconBack, IconSun, IconMoon, IconUser, IconLogout } from '../../components/ui/Icons';
import '../../styles/AdminLayout.css';

const NAV_KEYS = [
  { path: '/admin',             labelKey: 'admin.overview',          icon: '🏠', exact: true },
  { path: '/admin/invitations', labelKey: 'admin.invitations',       icon: '📨' },
  { path: '/admin/users',       labelKey: 'admin.users',             icon: '👥' },
  { path: '/admin/properties',  labelKey: 'admin.properties',        icon: '🏡' },
  { path: '/admin/partners',    labelKey: 'admin.partners',          icon: '🤝' },
  { path: '/admin/contracts',   labelKey: 'admin.contracts',         icon: '📄' },
  { path: '/admin/roles',       labelKey: 'admin.rolesAndPermissions', icon: '🎭', superadminOnly: true },
  { path: '/admin/audit-logs',  labelKey: 'auditLog.title',          icon: '📋' },
  { path: '/admin/site-config', labelKey: 'siteConfig.pageTitle',    icon: '🛠️',  superadminOnly: true },
  { path: '/admin/test-lab',    labelKey: 'testlab.title',           icon: '🧪',  superadminOnly: true },
  { path: '/admin/backups',     labelKey: 'backups.title',           icon: '💾',  superadminOnly: true },
];

const AdminLayout = ({ children }) => {
  const { user, logout, api } = useAuth();
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [version, setVersion] = useState(null);

  const isDark = theme === 'dark';
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const bgImage = isDark
    ? "url('/la_marina_alta_midnight_blue.webp')"
    : "url('/la_marina_alta_ocean.webp')";

  useEffect(() => {
    api.get('/version').then(r => setVersion(r.data.version)).catch(() => {});
  }, [api]);

  const doLogout = async () => {
    setConfirmLogout(false);
    await logout();
    navigate('/login');
  };

  const openProfile = () => { setSidebarOpen(false); setProfileDrawerOpen(true); };
  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark', true);

  return (
    <div className={`admin-layout ${isDark ? 'theme-dark' : 'theme-light'} ${sidebarOpen ? 'sidebar-open' : ''}`}
         style={{ backgroundImage: bgImage }}>

      <div className="admin-bg-overlay" />

      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <span className="admin-brand">VILLAPRESENCE</span>
          <span className="admin-badge-sm">Admin</span>
          {version && <span className="sidebar-version">v{version}</span>}
        </div>

        <nav className="sidebar-nav">
          {NAV_KEYS
            .filter(item => !item.superadminOnly || user?.role === 0)
            .map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sidebar-icon">{item.icon}</span>
                <span className="sidebar-label">{t(item.labelKey)}</span>
              </NavLink>
            ))
          }
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-name">{user?.username}</div>
            <div className="sidebar-user-role">{user?.role === 0 ? 'Superadmin' : 'Admin'}</div>
          </div>

          {/* Theme / profil / kilépés — csak mobilon látszik (desktopon a topbarban) */}
          <div className="sidebar-actions">
            <button className="sidebar-action" onClick={toggleTheme}>
              <span className="sidebar-action-icon">{isDark ? <IconSun /> : <IconMoon />}</span>
              <span>{isDark ? t('profile.switchLight') : t('profile.switchDark')}</span>
            </button>
            <button className="sidebar-action" onClick={openProfile}>
              <span className="sidebar-action-icon"><IconUser /></span>
              <span>{t('profile.myProfile')}</span>
            </button>
            <button className="sidebar-action sidebar-action--danger" onClick={() => { setSidebarOpen(false); setConfirmLogout(true); }}>
              <span className="sidebar-action-icon"><IconLogout /></span>
              <span>{t('common.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* FŐ TARTALOM */}
      <div className="admin-content">

        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <button className="admin-mobile-back" onClick={() => navigate('/dashboard')}
              aria-label={t('common.dashboard')} title={t('common.dashboard')}>
              <IconBack />
            </button>
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle sidebar">
              ☰
            </button>
          </div>
          <div className="topbar-right">
            <button className="ah-btn admin-desktop-back" onClick={() => navigate('/dashboard')}
              title={t('common.dashboard')} aria-label={t('common.dashboard')}>
              <IconBack />
            </button>
            <button className="ah-btn" onClick={toggleTheme}
              title={isDark ? t('profile.switchLight') : t('profile.switchDark')}
              aria-label={isDark ? t('profile.switchLight') : t('profile.switchDark')}>
              {isDark ? <IconSun /> : <IconMoon />}
            </button>
            <button className="ah-btn" onClick={() => setProfileDrawerOpen(true)}
              title={t('profile.myProfile')} aria-label={t('profile.myProfile')}>
              <IconUser />
            </button>
            <button className="ah-btn ah-btn--danger" onClick={() => setConfirmLogout(true)}
              title={t('common.logout')} aria-label={t('common.logout')}>
              <IconLogout />
            </button>
          </div>
        </header>

        <main className="admin-page-content">
          {children}
        </main>
      </div>

      {/* PROFILE DRAWER */}
      {profileDrawerOpen && user && (
        <ProfileDrawer
          userId={user.id}
          onClose={() => setProfileDrawerOpen(false)}
        />
      )}

      {/* LOGOUT CONFIRM */}
      {confirmLogout && (
        <div className="ah-confirm-overlay" onClick={() => setConfirmLogout(false)}>
          <div className="ah-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="ah-confirm-text">{t('common.logoutConfirm') || 'Are you sure you want to log out?'}</p>
            <div className="ah-confirm-actions">
              <button type="button" className="ah-confirm-btn" onClick={() => setConfirmLogout(false)}>
                {t('common.no') || 'No'}
              </button>
              <button type="button" className="ah-confirm-btn ah-confirm-btn--danger" onClick={doLogout}>
                {t('common.yes') || 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
